import React, { useCallback, useEffect, useState } from "react";
import { AuthorizedLayout } from "../Layouts/AuthorizedLayout";
import BoxHeader from "../../Components/Box/BoxHeader";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Box,
  Grid,
  FormLabel,
} from "@mui/material";
import { CustomTablePagination } from "../../Components/Pagination/CustomTablePagination";
import TableSearch from "../../Components/Search/TableSearch";
import { useDispatch } from "react-redux";
import { AntTab, AntTabs } from "../../Components/Tab/AntTab";
import { PermissionCheck } from "../../App/PermissionCheck";
import { GetApiMethod, PostApiMethod, PutApiMethod, DeleteApiMethod } from "../../Utils/ApiService";
import { showLoader } from "../../Store/loaderSlice";
import { showToaster } from "../../Store/toastSlice";
import { CustomModal, ModalStyle } from "../../Components/ModalStyle";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import MenuGroup from "../../Components/DropDown/MenuGroup";
import { debounce } from "lodash";
import { useNavigate } from "react-router-dom";

const PostManagement = () => {
  const [postList, setPostList] = useState({ rows: [], totalCount: 0 });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [formModal, setFormModal] = useState(false);
  const [editPost, setEditPost] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  

  // Fetch posts
  const getPosts = useCallback(async () => {
    try {
      dispatch(showLoader({ showLoader: true }));
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        search: searchTerm.trim(),
        sort: "createdAt",
        sortDirection: "DESC",
      });
      const res = await GetApiMethod(`posts?${params.toString()}`, {
        headers: { "isweb": "true" }
      });
      const responseData = res.data.data || res.data;
      setPostList({
        rows: responseData.rows || [],
        totalCount: responseData.count || 0,
      });
    } catch (err) {
      dispatch(
        showToaster({
          dialogBgColor: "bg-danger",
          dialogMsg: "Failed to fetch posts",
          showDialog: true,
          timer: 3000,
        })
      );
    } finally {
      dispatch(showLoader({ showLoader: false }));
    }
  }, [dispatch, page, rowsPerPage, searchTerm]);

  const fetchPostsDebounced = useCallback(
    debounce(() => {
      getPosts();
    }, 500),
    [getPosts]
  );

  useEffect(() => {
    getPosts();
    return () => fetchPostsDebounced.cancel();
  }, [getPosts, fetchPostsDebounced]);

  // Save or update post
  const handleSavePost = async (values, id) => {
    try {
      dispatch(showLoader({ showLoader: true }));
      const payload = {
        content: values.content,
        postType: values.postType,
        locationName: values.locationName,
        latitude: values.latitude || null,
        longitude: values.longitude || null,
        tagId: editPost?.tagId || null,
        userId: "admin",
      };

      if (id) {
        await PutApiMethod(`posts/${id}`, payload);
        dispatch(
          showToaster({
            dialogBgColor: "bg-success",
            dialogMsg: `Post updated successfully`,
            showDialog: true,
            timer: 3000,
          })
        );
      } else {
        await PostApiMethod("post", payload);
        dispatch(
          showToaster({
            dialogBgColor: "bg-success",
            dialogMsg: `Post created successfully`,
            showDialog: true,
            timer: 3000,
          })
        );
      }
      resetForm();
      await getPosts();
    } catch (err) {
      dispatch(
        showToaster({
          dialogBgColor: "bg-danger",
          dialogMsg:
            err?.response?.data?.message ||
            `Failed to ${id ? "update" : "create"} post`,
          showDialog: true,
          timer: 3000,
        })
      );
    } finally {
      dispatch(showLoader({ showLoader: false }));
    }
  };

  const resetForm = useCallback(() => {
    setFormModal(false);
    setEditPost(null);
  }, []);

  const handleDeletePost = async (id) => {
    try {
      dispatch(showLoader({ showLoader: true }));
      await DeleteApiMethod(`posts/${id}`);
      await getPosts();
      dispatch(
        showToaster({
          dialogBgColor: "bg-success",
          dialogMsg: `Post deleted successfully`,
          showDialog: true,
          timer: 3000,
        })
      );
    } catch (err) {
      dispatch(
        showToaster({
          dialogBgColor: "bg-danger",
          dialogMsg: err?.response?.data?.message || "Failed to delete post",
          showDialog: true,
          timer: 3000,
        })
      );
    } finally {
      dispatch(showLoader({ showLoader: false }));
    }
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setPage(0);
    fetchPostsDebounced();
  };

  return (
    <AuthorizedLayout>
      <BoxHeader dashboard_name="Post Management" button_show={false}>
      <AntTabs value={0}>
        <AntTab label="All Posts" className="tab-menu" />
      </AntTabs>
    </BoxHeader>


      <TableSearch
        value={searchTerm}
        onChange={handleSearchChange}
        placeholder="Search posts..."
      />

      <TableContainer className="mt-2">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Content</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Tags</TableCell>
              <TableCell>Likes</TableCell>
              <TableCell>Comments</TableCell>
              <TableCell>Location</TableCell> 
              <TableCell>Detail</TableCell>  
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {postList.rows.map((post) => (
              <TableRow key={post.id}>
                <TableCell>
                  {post.postUserDetails
                    ? `${post.postUserDetails.firstName} ${post.postUserDetails.lastName}`
                    : "Unknown"}
                </TableCell>
                <TableCell>{post.content}</TableCell>
                <TableCell>{post.postType}</TableCell>
                <TableCell>
                  {post.postTagDetails ? (
                    <span
                      className="badge me-1"
                      style={{
                        background: "rgb(9, 187, 233)",
                        color: "#fff",
                        fontWeight: "500",
                        padding: "5px 10px",
                        borderRadius: "8px",
                      }}
                    >
                      {post.postTagDetails.name}
                    </span>
                  ) : (
                    <span>-</span>
                  )}
                </TableCell>
                <TableCell>{post.likeCount}</TableCell>
                <TableCell>{post.commentCount}</TableCell>
                <TableCell>{post.postLocationDetails ? post.postLocationDetails.name : "-"}</TableCell>
                <TableCell
                  sx={{
                    textDecoration: "underline",
                    color: "blue",
                    cursor: "pointer",
                  }}
                  onClick={() => navigate(`/posts/${post.id}`)}
                >
                View
                </TableCell>

                <TableCell align="right">
                  <MenuGroup
                    id={post.id}
                    view={false}
                    edit={true}
                    delete={true}
                    clickEdit={() => {
                      setEditPost(post);
                      setFormModal(true);
                    }}
                    clickDelete={() => handleDeletePost(post.id)}
                  />
                </TableCell>
              </TableRow>
            ))}
            {postList.rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No Data
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <CustomTablePagination
        count={postList.totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
      />

      {/* Modal for create/edit post */}
      <CustomModal
        open={formModal}
        onClose={resetForm}
        header={`${editPost?.id ? "Edit" : "Create"} Post`}
        style={ModalStyle({ width: 600, height: "auto" })}
      >
        <Formik
          key={editPost?.id || "new"}
          initialValues={{
            content: editPost?.content || "",
            postType: editPost?.postType || "text",
            locationName: editPost?.locationName || "",
            latitude: editPost?.latitude || "",
            longitude: editPost?.longitude || "",
            tagName: editPost?.postTagDetails?.name || "",
          }}
          validationSchema={Yup.object({
            content: Yup.string().trim().required("Content is required"),
            postType: Yup.string().oneOf(["post", "story"], "Post type must be one of: post, story").required("Post type is required"),
            locationName: Yup.string().required("Location is required"),
            latitude: Yup.number()
              .nullable()
              .typeError("Latitude must be a number"),
            longitude: Yup.number()
              .nullable()
              .typeError("Longitude must be a number"),
            tagName: Yup.string().required("Tag is required"),
          })}
          onSubmit={async (values) => {
            await handleSavePost(values, editPost?.id);
          }}
          enableReinitialize
        >
          {({ handleSubmit, handleChange, values, errors, touched }) => (
            <Form onSubmit={handleSubmit}>
              <Box className="modal-body">
                <Grid container spacing={2} className="p-3">
                  <Grid item md={12}>
                    <FormLabel className="fw-bold">
                      Content:<sup className="text-danger">*</sup>
                    </FormLabel>
                    <TextField
                      className="w-100"
                      placeholder="Enter post content..."
                      variant="standard"
                      name="content"
                      value={values.content}
                      onChange={handleChange}
                    />
                    {touched.content && errors.content && (
                      <div className="text-danger">{errors.content}</div>
                    )}
                  </Grid>
                  <Grid item md={6}>
                    <FormLabel className="fw-bold">
                      Post Type:<sup className="text-danger">*</sup>
                    </FormLabel>
                    <TextField
                      className="w-100"
                      placeholder="Enter post type..."
                      variant="standard"
                      name="postType"
                      value={values.postType}
                      onChange={handleChange}
                    />
                    {touched.postType && errors.postType && (
                      <div className="text-danger">{errors.postType}</div>
                    )}
                  </Grid>
                  <Grid item md={6}>
                    <FormLabel className="fw-bold">
                      Tag:<sup className="text-danger">*</sup>
                    </FormLabel>
                    <TextField
                      className="w-100"
                      placeholder="Enter tag..."
                      variant="standard"
                      name="tagName"
                      value={values.tagName}
                      onChange={handleChange}
                    />
                    {touched.tagName && errors.tagName && (
                      <div className="text-danger">{errors.tagName}</div>
                    )}
                  </Grid>
                  <Grid item md={6}>
                    <FormLabel className="fw-bold">
                      Location Name:<sup className="text-danger">*</sup>
                    </FormLabel>
                    <TextField
                      className="w-100"
                      placeholder="Enter location..."
                      variant="standard"
                      name="locationName"
                      value={values.locationName}
                      onChange={handleChange}
                    />
                    {touched.locationName && errors.locationName && (
                      <div className="text-danger">{errors.locationName}</div>
                    )}
                  </Grid>
                  <Grid item md={3}>
                    <FormLabel className="fw-bold">Latitude:</FormLabel>
                    <TextField
                      className="w-100"
                      placeholder="Latitude"
                      variant="standard"
                      name="latitude"
                      value={values.latitude}
                      onChange={handleChange}
                    />
                    {touched.latitude && errors.latitude && (
                      <div className="text-danger">{errors.latitude}</div>
                    )}
                  </Grid>
                  <Grid item md={3}>
                    <FormLabel className="fw-bold">Longitude:</FormLabel>
                    <TextField
                      className="w-100"
                      placeholder="Longitude"
                      variant="standard"
                      name="longitude"
                      value={values.longitude}
                      onChange={handleChange}
                    />
                    {touched.longitude && errors.longitude && (
                      <div className="text-danger">{errors.longitude}</div>
                    )}
                  </Grid>
                </Grid>
                <Box className="d-flex justify-content-end mt-3 p-3">
                  <button className="btn btn-danger" type="submit">
                    Save
                  </button>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </CustomModal>
    </AuthorizedLayout>
  );
};

export default PostManagement;
